import { Grid, Typography } from '@mui/material';
import { ProceedSaveLater, SubHeading } from './components';
import { Formik, validateYupSchema, yupToFormErrors } from 'formik';
import MFSelectField from '../../lib/formik/SelectField';
import { Applicant, Broker, BrokerList, DPNameType } from '../../redux-store/types/api-types';
import {
  DLCLMasters,
  grossAnnualMasters,
  LAW_TYPES,
  occupationDetailsMasters,
  PEPsMasters,
  SAVE_LATER_VALIDATION_CHECK,
  USER_ROLES,
} from '../../utils/constant';
import {
  // eslint-disable-next-line
  allowOnlyNumbers,
  applicationComparison,
  clearclIdField,
  cleardpIdField,
  currencyConversion,
  getApplicantName,
  getCurrentStep,
  getclIdField,
  getdpIdField,
  isCDSL,
  isFirstApplicant,
  minNetworthDate,
  nameOfBrokerExists,
  navigateToNextStep,
  otherNameOfBroker,
  sortListKeepOthersLast,
  statusRoleBased,
} from '../../utils/utilityFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { useEffect, useState } from 'react';
import { updateApplication } from '../../redux-store/actions/application';
import { useHistory, useLocation } from 'react-router';
import { KYCDetailsSchema } from '../../utils/schema';
import { MFTextField } from '../../lib/formik';
import { useSnackbar } from 'notistack';
import { SearchableSelect } from '../../lib/formik/searchSelectField';
import { useFeatureToggles } from '../../utils/useDataMdms';
import { useDPNameListData } from '../../utils/useDPNameList';
import FieldValidationNote from './FieldValidationNote';
import { DatePicker } from '../../lib/formik/DatePicker';

type Values = {
  applicants: Partial<Applicant & { otherNameOfBroker: string; applicant_type: string }>[];
  saveType: string;
};

const initialValues: Values = {
  applicants: [
    {
      occupationDetails: '',
      grossAnnualIncome: '',
      politicallyExposedPersonStatus: 'Not Applicable',
      ckycNo: '',
      dlclId: '',
      dpId: '',
      clId: '',
      nameOfBroker: '',
      otherNameOfBroker: '',
      applicant_type: '',
      netWorth: '',
      netWorthDate: '',
      lawType: '',
      broker_id: null,
    },
  ],
  saveType: 'save and proceed',
};

const KYCDetails = ({
  applicant,
  index,
  values,
  // brokers,
  ifTopUp,
  dpBroker,
}: {
  applicant: string;
  index: number;
  values: Values;
  // brokers: Broker[];
  ifTopUp: boolean;
  dpBroker: DPNameType[];
}): JSX.Element => {
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const { application } = useSelector((state: RootStateType) => state.application);
  return (
    <>
      <SubHeading>Additional KYC Details of {applicant} Applicant</SubHeading>
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.occupationDetails`}
          label="Occupation Details"
          required
          items={occupationDetailsMasters.map((occupation) => ({
            key: occupation,
            value: occupation,
          }))}
          disabled={ifTopUp}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.grossAnnualIncome`}
          label="Gross Annual Income"
          items={grossAnnualMasters.map((grossIncome) => ({
            key: grossIncome,
            value: grossIncome,
          }))}
          disabled={ifTopUp}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFTextField
          name={`applicants.${index}.netWorth`}
          label="Net Worth"
          placeholder="Enter Net Worth"
        />
        {values.applicants[index].netWorth && !isNaN(Number(values.applicants[index].netWorth)) && (
          <Typography sx={{ fontSize: 13 }}>
            {currencyConversion(values.applicants[index].netWorth)}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label={'Net worth as on'}
          inputLabelStyles={{
            transform: 'unset',
            fontSize: 14,
            fontWeight: 500,
            color: 'rgba(0,0,0,0.7)',
          }}
          placeholder={'Enter Net worth as on'}
          name={`applicants.${index}.netWorthDate`}
          value={values.applicants[index].netWorthDate}
          minDate={minNetworthDate(application?.createdAt || '')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.politicallyExposedPersonStatus`}
          label="Politically Exposed Person (PEP) Status"
          required
          items={PEPsMasters.map((pep) => ({ key: pep, value: pep }))}
          disabled={ifTopUp}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFTextField
          name={`applicants.${index}.ckycNo`}
          label="CKYC Number"
          placeholder={'Enter CKYC number'}
          disabled={[USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role) || ifTopUp}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.dlclId`}
          label={`Demat Account Details (for allotment of units)`}
          required={isFirstApplicant(values.applicants[index]?.applicant_type || '')}
          items={Object.keys(DLCLMasters).map((Id) => ({
            key: DLCLMasters[Id],
            value: Id,
          }))}
          disabled={ifTopUp}
        />
      </Grid>
      {getdpIdField(values.applicants[index].dlclId || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={`applicants.${index}.dpId`}
            label={`${isCDSL(values.applicants[index].dlclId || '') ? 'DPCL ID' : 'DP ID'}`}
            required={isFirstApplicant(values.applicants[index]?.applicant_type || '')}
            placeholder={`Enter ${
              isCDSL(values.applicants[index].dlclId || '') ? 'DPCL ID' : 'DP ID'
            }`}
            inputProps={{ style: { textTransform: 'uppercase' } }}
            disabled={ifTopUp}
          />
        </Grid>
      )}
      {getclIdField(values.applicants[index].dlclId || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={`applicants.${index}.clId`}
            label={`CL ID`}
            required={isFirstApplicant(values.applicants[index]?.applicant_type || '')}
            placeholder="Enter CL ID"
            disabled={ifTopUp}
          />
        </Grid>
      )}
      {/* {getdpIdField(values.applicants[index].dlclId || '') && (
        <Grid item xs={12} sm={6}>
          <SearchableSelect
            name={`applicants.${index}.nameOfBroker`}
            label="Name of Broker/Depository Participant (DP)"
            items={sortListKeepOthersLast(brokers || [])?.map((broker: Broker) => ({
              key: broker.value,
              value: broker.key,
            }))}
            searchFieldPlaceholder={'Search Broker/Depository Participant'}
            disabled={ifTopUp}
          />
        </Grid>
      )} */}
      {getdpIdField(values.applicants[index].dlclId || '') && (
        <Grid item xs={12} sm={6}>
          <SearchableSelect
            name={`applicants.${index}.broker_id`}
            label="Name of Broker/Depository Participant (DP)"
            items={dpBroker
              .filter(
                (broker) =>
                  broker?.isActive || broker?.id == application?.applicants[index]?.broker_id
              )
              .map((broker: DPNameType) => ({
                value: Number(broker.id),
                key: broker.brokerName ?? '',
              }))}
            searchFieldPlaceholder="Search Broker/Depository Participant"
          />
        </Grid>
      )}
      {/* {getdpIdField(values.applicants[index].dlclId || '') &&
        otherNameOfBroker(values.applicants[index].nameOfBroker || '') && (
          <Grid item xs={12} sm={6}>
            <MFTextField
              name={`applicants.${index}.otherNameOfBroker`}
              label="Name of Broker"
              required
              placeholder="Enter Name of Broker"
              disabled={ifTopUp}
            />
          </Grid>
        )} */}
      <Grid item xs={12} sm={6}>
        <MFSelectField
          name={`applicants.${index}.lawType`}
          label="Law Under Which Formed/Incorporated"
          items={Object.keys(LAW_TYPES)
            .filter((item) => item === 'individual')
            .map((item) => ({
              key: LAW_TYPES[item],
              value: item,
            }))}
        />
      </Grid>
    </>
  );
};

export default function AdditionalKYCDetails(): JSX.Element {
  const [kycDetails, setkycDetails] = useState(initialValues);
  const { application } = useSelector((store: RootStateType) => store.application);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  // const [brokerList, setBrokerList] = useState<BrokerList>();
  // const { BrokerList } = useMdmsBrokerListData();
  const { toggler } = useFeatureToggles();
  const location = useLocation();
  const path = location.pathname;
  // const [dpbrokerList, dpsetBrokerList] = useState<DPNameType[]>([]);

  // useEffect(() => {
  //   (async function () {
  //     try {
  //       setBrokerList(BrokerList as BrokerList);
  //     } catch (e) {
  //       console.error((e as Error).message);
  //     }
  //   })();
  // }, [BrokerList]);

  // useEffect(() => {
  //   const fetchBrokerList = async () => {
  //     try {
  //       const response = (await dispatch(getDPNameList())) as unknown as DPNameType[];
  //       dpsetBrokerList(response);
  //     } catch (e) {
  //       console.error((e as Error).message);
  //     }
  //   };
  //   fetchBrokerList();
  // }, []);

  const dpbrokerList = useDPNameListData();

  useEffect(() => {
    (async function () {
      try {
        const { applicants: exisitingApplicants = [] } = application || {};
        setkycDetails({
          ...kycDetails,
          applicants: exisitingApplicants.map((applicant) => {
            return {
              occupationDetails: applicant.occupationDetails || '',
              grossAnnualIncome: applicant.grossAnnualIncome || '',
              politicallyExposedPersonStatus: applicant.politicallyExposedPersonStatus
                ? applicant.politicallyExposedPersonStatus
                : [USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role)
                ? ''
                : 'Not Applicable',
              ckycNo: applicant.ckycNo || '',
              dlclId: applicant.dlclId ? applicant.dlclId : '',
              dpId: applicant.dpId || '',
              clId: applicant.clId || '',
              // nameOfBroker: applicant.nameOfBroker
              //   ? brokerList?.broker_list &&
              //     nameOfBrokerExists(
              //       brokerList?.broker_list as Broker[],
              //       applicant.nameOfBroker || ''
              //     )
              //     ? applicant.nameOfBroker
              //     : 'others'
              //   : '',
              // otherNameOfBroker:
              //   applicant.nameOfBroker &&
              //   brokerList?.broker_list &&
              //   nameOfBrokerExists(
              //     brokerList?.broker_list as Broker[],
              //     applicant.nameOfBroker || ''
              //   )
              //     ? ''
              //     : applicant.nameOfBroker,
              applicant_type: applicant?.applicant_type || '',
              netWorth: applicant.netWorth || '',
              netWorthDate: applicant.netWorthDate || '',
              lawType: applicant?.lawType || '',
              broker_id: applicant.broker_id || null,
            };
          }),
        });
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, [application]); //, brokerList]);

  const handleSubmit = async (values: Values) => {
    try {
      const { applicants, saveType } = values;
      const {
        applicants: exisitingApplicants = [],
        id,
        applicant1ReferenceId = '',
        currentStep,
        applicationNumber,
        topupApplicationNumber,
        isTopup,
      } = application || {};
      const updatedApplicants = exisitingApplicants.map((applicant, index) => {
        const {
          otherNameOfBroker: othersNameOfBroker,
          // eslint-disable-next-line
          applicant_type,
          ...rest
        } = applicants[index];
        return {
          ...applicant,
          ...rest,
          clId: clearclIdField(applicants[index].dlclId || '') ? '' : applicants[index].clId,
          dpId: cleardpIdField(applicants[index].dlclId || '')
            ? ''
            : applicants[index]?.dpId?.toUpperCase(),
          // nameOfBroker: cleardpIdField(applicants[index].dlclId || '')
          //   ? ''
          //   : otherNameOfBroker(applicants[index].nameOfBroker || '')
          //   ? othersNameOfBroker
          //   : applicants[index].nameOfBroker,
          netWorthDate: applicants[index].netWorthDate ? applicants[index].netWorthDate : null,
          lawType: applicants[index].lawType,
          broker_id: cleardpIdField(applicants[index].dlclId || '')
            ? 0
            : applicants[index].broker_id,
        };
      });
      const isSaveLater = saveType !== 'save and proceed';
      const checkApplication = applicationComparison(application, {
        ...application,
        applicants: updatedApplicants,
        currentStep:
          !!currentStep &&
          currentStep >
            getCurrentStep(
              application?.applicationType || '',
              path,
              !!isTopup,
              isSaveLater,
              application?.createdAt,
              toggler
            ) -
              1
            ? currentStep
            : getCurrentStep(
                application?.applicationType || '',
                path,
                !!isTopup,
                isSaveLater,
                application?.createdAt,
                toggler
              ),
      });
      if (id && !checkApplication) {
        setLoading(true);
        await dispatch(
          updateApplication({
            body: {
              ...application,
              applicants: updatedApplicants,
              status: statusRoleBased(role, application),
              currentStep: getCurrentStep(
                application?.applicationType || '',
                path,
                !!isTopup,
                isSaveLater,
                application?.createdAt,
                toggler
              ),
              ...((!SAVE_LATER_VALIDATION_CHECK as boolean) && { saveForLater: isSaveLater }),
            },
            applicationId: id,
            ...(isSaveLater && { toastMessage: '' }),
          })
        );
        history.push(
          navigateToNextStep(
            path,
            role,
            id || '',
            isSaveLater,
            applicant1ReferenceId || '',
            application?.applicationType || '',
            !!isTopup,
            application?.createdAt,
            toggler
          ),
          {
            id,
            applicant1ReferenceId,
            topUpApplication: application?.topupInitiated,
          }
        );
      } else if (checkApplication) {
        if (isSaveLater) {
          enqueueSnackbar(
            `Application ${
              application?.topupInitiated ? topupApplicationNumber : applicationNumber
            } - ` + ' Saved successfully',
            {
              variant: 'success',
              autoHideDuration: 3000,
            }
          );
        }
        history.push(
          navigateToNextStep(
            path,
            role,
            id || '',
            isSaveLater,
            applicant1ReferenceId || '',
            application?.applicationType || '',
            !!isTopup,
            application?.createdAt,
            toggler
          ),
          {
            id,
            applicant1ReferenceId,
            topUpApplication: application?.topupInitiated,
          }
        );
      }
    } catch (e) {
      setLoading(false);
      console.error((e as Error).message);
    }
  };

  return (
    <Formik
      initialValues={kycDetails}
      onSubmit={handleSubmit}
      validate={(values: Values) => {
        try {
          validateYupSchema(
            values,
            KYCDetailsSchema(
              // brokerList?.broker_list as Broker[],
              application?.createdAt || '',
              toggler
            ),
            true,
            values
          );
        } catch (e) {
          return yupToFormErrors(e);
        }
      }}
      enableReinitialize={true}>
      {({ handleSubmit, values, setValues }) => (
        <Grid
          container
          rowSpacing={1}
          // columnSpacing={5}
          sx={{
            width: '100%',
            ml: 0,
            '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
          }}
          component="form"
          noValidate
          onSubmit={handleSubmit}>
          {values.applicants.map((_, index) => (
            <KYCDetails
              applicant={getApplicantName(index + 1)}
              key={index}
              index={index}
              values={values}
              // brokers={brokerList?.broker_list as Broker[]}
              ifTopUp={application?.topupInitiated || false}
              dpBroker={dpbrokerList}
            />
          ))}
          <FieldValidationNote />
          <ProceedSaveLater
            saveLater={() => {
              setValues({
                ...values,
                saveType: 'save for later',
              });
            }}
            saveAndProceed={() => {
              setValues({
                ...values,
                saveType: 'save and proceed',
              });
            }}
            loader={loading}
            clickedButton={values.saveType}
          />
        </Grid>
      )}
    </Formik>
  );
}
